import React, { useState, useEffect } from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';
import Input from '../../../components/ui/Input';
import DynamicDropdown from '../../../components/ui/DynamicDropdown';
import dynamicOptionsService from '../../../utils/dynamicOptionsService';
import { customerService } from '../../../utils/dataServices';
import { useOrganization } from '../../../contexts/OrganizationContext';

const EditCustomerModal = ({ isOpen, onClose, customer, onSave }) => {
  const { currentOrganization } = useOrganization();
  const [formData, setFormData] = useState(null);
  const [errors, setErrors] = useState({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [leadSources, setLeadSources] = useState([]);
  const [primaryServices, setPrimaryServices] = useState([]);
  const [loadingOptions, setLoadingOptions] = useState(true);

  const customerTypeOptions = [
    { value: 'individual', label: 'Individual Customer' },
    { value: 'organization', label: 'Organization/Business' }
  ];

  const customerTierOptions = [
    { value: 'Basic', label: 'Basic' },
    { value: 'Standard', label: 'Standard' },
    { value: 'Premium', label: 'Premium' }
  ];

  const industryOptions = [
    { value: '', label: 'Select Industry' },
    { value: 'Residential', label: 'Residential' },
    { value: 'Commercial', label: 'Commercial' },
    { value: 'Industrial', label: 'Industrial' },
    { value: 'Healthcare', label: 'Healthcare' },
    { value: 'Education', label: 'Education' },
    { value: 'Retail', label: 'Retail' },
    { value: 'Hospitality', label: 'Hospitality' },
    { value: 'Manufacturing', label: 'Manufacturing' },
    { value: 'Technology', label: 'Technology' },
    { value: 'Other', label: 'Other' }
  ];

  const organizationSizeOptions = [
    { value: '', label: 'Select Size' },
    { value: '1-10', label: '1-10 employees' },
    { value: '11-50', label: '11-50 employees' },
    { value: '51-200', label: '51-200 employees' },
    { value: '201-500', label: '201-500 employees' },
    { value: '500+', label: '500+ employees' }
  ];

  const contactMethodOptions = [
    { value: 'email', label: 'Email' },
    { value: 'phone', label: 'Phone' },
    { value: 'sms', label: 'SMS' }
  ];

  // Load dynamic options on mount
  useEffect(() => {
    const loadOptions = async () => {
      if (!currentOrganization?.id) return;

      try {
        setLoadingOptions(true);
        
        const [sourcesData, servicesData] = await Promise.all([
          dynamicOptionsService.getLeadSources(currentOrganization.id),
          dynamicOptionsService.getPrimaryServices(currentOrganization.id)
        ]);
        
        setLeadSources(sourcesData);
        setPrimaryServices(servicesData);
      } catch (error) {
        console.error('Error loading dynamic options:', error);
      } finally {
        setLoadingOptions(false);
      }
    };

    if (isOpen) {
      loadOptions();
    }
  }, [isOpen, currentOrganization?.id]);

  useEffect(() => {
    const loadCustomerData = async () => {
      if (customer && !loadingOptions) {
        // Find matching lead source and primary service objects
        const leadSource = customer.lead_source_id 
          ? leadSources.find(ls => ls.id === customer.lead_source_id)
          : null;
        
        const primaryService = customer.primary_service_id 
          ? primaryServices.find(ps => ps.id === customer.primary_service_id)
          : null;

        setFormData({ 
          name: customer.name || '',
          email: customer.email || '',
          phone: customer.phone || '',
          address: customer.address || '',
          city: customer.city || '',
          state: customer.state || '',
          zipCode: customer.zipCode || customer.zip_code || '',
          customerTier: customer.customerTier || customer.customer_tier || 'Standard',
          customerType: customer.customerType || customer.customer_type || 'individual',
          organizationName: customer.organizationName || customer.organization_name || '',
          website: customer.website || '',
          industry: customer.industry || '',
          organizationSize: customer.organizationSize || customer.organization_size || '',
          serviceType: customer.serviceType || customer.service_type || '',
          notes: customer.notes || '',
          preferredContactMethod: customer.preferredContactMethod || customer.preferred_contact_method || 'email',
          estimatedValue: customer.estimatedValue || customer.estimated_value || '',
          leadSource: leadSource,
          primaryService: primaryService
        });
      }
    };

    loadCustomerData();
  }, [customer, leadSources, primaryServices, loadingOptions]);

  if (!isOpen || !formData) return null;

  const handleInputChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
    if (errors[field]) {
      setErrors(prev => ({ ...prev, [field]: '' }));
    }
  };

  // Handle adding new lead source
  const handleAddLeadSource = async (name) => {
    try {
      const newSource = await dynamicOptionsService.createLeadSource(name, currentOrganization?.id);
      setLeadSources(prev => [...prev, newSource]);
      return newSource;
    } catch (error) {
      console.error('Error adding lead source:', error);
      throw error;
    }
  };

  // Handle adding new primary service
  const handleAddPrimaryService = async (name) => {
    try {
      const newService = await dynamicOptionsService.createPrimaryService(name, currentOrganization?.id);
      setPrimaryServices(prev => [...prev, newService]);
      return newService;
    } catch (error) {
      console.error('Error adding primary service:', error);
      throw error;
    }
  };

  const validateForm = () => {
    const newErrors = {};

    if (!formData.name.trim()) {
      newErrors.name = 'Customer name is required';
    }

    if (formData.customerType === 'organization' && !formData.organizationName.trim()) {
      newErrors.organizationName = 'Organization name is required';
    }

    if (formData.customerType === 'organization' && formData.website && !/^(https?:\/\/)?(www\.)?[a-zA-Z0-9]([a-zA-Z0-9-]{0,61}[a-zA-Z0-9])?\.[a-zA-Z]{2,}([\\/\w\.-]*)*\/?$/.test(formData.website)) {
      newErrors.website = 'Please enter a valid website URL';
    }

    if (!formData.email.trim()) {
      newErrors.email = 'Email is required';
    } else if (!/\S+@\S+\.\S+/.test(formData.email)) {
      newErrors.email = 'Please enter a valid email address';
    }

    if (!formData.phone.trim()) {
      newErrors.phone = 'Phone number is required';
    } else if (!/^\(\d{3}\) \d{3}-\d{4}$/.test(formData.phone)) {
      newErrors.phone = 'Please enter a valid phone number (XXX) XXX-XXXX';
    }

    // Address fields are no longer required

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const formatPhoneNumber = (value) => {
    const phoneNumber = value.replace(/\D/g, '');
    if (phoneNumber.length <= 3) {
      return phoneNumber;
    } else if (phoneNumber.length <= 6) {
      return `(${phoneNumber.slice(0, 3)}) ${phoneNumber.slice(3)}`;
    } else {
      return `(${phoneNumber.slice(0, 3)}) ${phoneNumber.slice(3, 6)}-${phoneNumber.slice(6, 10)}`;
    }
  };

  const handlePhoneChange = (e) => {
    const formatted = formatPhoneNumber(e.target.value);
    handleInputChange('phone', formatted);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!validateForm()) {
      return;
    }

    setIsSubmitting(true);
    
    try {
      // Prepare data for submission - convert objects to IDs
      const submitData = {
        ...formData,
        leadSourceId: formData.leadSource?.id || null,
        primaryServiceId: formData.primaryService?.id || null,
        // Remove the object versions
        leadSource: undefined,
        primaryService: undefined
      };

      // Use actual customerService to update the customer
      const result = await customerService.updateContact(customer.id, submitData);
      
      if (result.success) {
        onSave(result.data);
        onClose();
      } else {
        console.error('Error updating customer:', result.error);
        // You might want to show an error message to the user here
      }
    } catch (error) {
      console.error('Error updating customer:', error);
      // You might want to show an error message to the user here
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-300 p-4">
      <div className="bg-card rounded-lg shadow-xl w-full max-w-2xl max-h-[90vh] overflow-y-auto">
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b border-border">
          <div className="flex items-center space-x-3">
            <div className="w-10 h-10 bg-primary-100 rounded-full flex items-center justify-center">
              <Icon name="User" size={20} className="text-primary" />
            </div>
            <div>
              <h2 className="text-xl font-semibold text-foreground">Edit Customer</h2>
              <p className="text-sm text-text-secondary">Update customer profile</p>
            </div>
          </div>
          <Button variant="ghost" onClick={onClose} className="p-2">
            <Icon name="X" size={20} />
          </Button>
        </div>

        {/* Form */}
        <form onSubmit={handleSubmit} className="p-6 space-y-6">
          {/* Basic Information */}
          <div>
            <h3 className="text-lg font-medium text-foreground mb-4">Basic Information</h3>
            
            {/* Customer Type Selector */}
            <div className="mb-4">
              <label className="block text-sm font-medium text-foreground mb-2">
                Customer Type *
              </label>
              <div className="grid grid-cols-2 gap-4">
                {customerTypeOptions.map((option) => (
                  <label key={option.value} className="flex items-center space-x-3 cursor-pointer">
                    <input
                      type="radio"
                      name="customerType"
                      value={option.value}
                      checked={formData.customerType === option.value}
                      onChange={(e) => handleInputChange('customerType', e.target.value)}
                      className="w-4 h-4 text-primary focus:ring-primary border-border"
                    />
                    <span className="text-sm text-foreground">{option.label}</span>
                  </label>
                ))}
              </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {/* Name field - dynamic label based on customer type */}
              <div>
                <label className="block text-sm font-medium text-foreground mb-2">
                  {formData.customerType === 'organization' ? 'Contact Person Name *' : 'Customer Name *'}
                </label>
                <Input
                  type="text"
                  value={formData.name}
                  onChange={(e) => handleInputChange('name', e.target.value)}
                  placeholder={formData.customerType === 'organization' ? 'Enter contact person name' : 'Enter customer name'}
                  className={errors.name ? 'border-error' : ''}
                />
                {errors.name && (
                  <p className="mt-1 text-sm text-error">{errors.name}</p>
                )}
              </div>

              {/* Organization Name - only for organizations */}
              {formData.customerType === 'organization' && (
                <div>
                  <label className="block text-sm font-medium text-foreground mb-2">
                    Organization Name *
                  </label>
                  <Input
                    type="text"
                    value={formData.organizationName}
                    onChange={(e) => handleInputChange('organizationName', e.target.value)}
                    placeholder="Enter organization name"
                    className={errors.organizationName ? 'border-error' : ''}
                  />
                  {errors.organizationName && (
                    <p className="mt-1 text-sm text-error">{errors.organizationName}</p>
                  )}
                </div>
              )}

              <div>
                <label className="block text-sm font-medium text-foreground mb-2">
                  Customer Tier
                </label>
                <select
                  value={formData.customerTier}
                  onChange={(e) => handleInputChange('customerTier', e.target.value)}
                  className="w-full border border-border rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                >
                  {customerTierOptions.map((option) => (
                    <option key={option.value} value={option.value}>
                      {option.label}
                    </option>
                  ))}
                </select>
              </div>
            </div>
          </div>

          {/* Contact Information */}
          <div>
            <h3 className="text-lg font-medium text-foreground mb-4">Contact Information</h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-foreground mb-2">
                  Email Address *
                </label>
                <Input
                  type="email"
                  value={formData.email}
                  onChange={(e) => handleInputChange('email', e.target.value)}
                  placeholder="customer@example.com"
                  className={errors.email ? 'border-error' : ''}
                />
                {errors.email && (
                  <p className="mt-1 text-sm text-error">{errors.email}</p>
                )}
              </div>

              <div>
                <label className="block text-sm font-medium text-foreground mb-2">
                  Phone Number *
                </label>
                <Input
                  type="tel"
                  value={formData.phone}
                  onChange={handlePhoneChange}
                  placeholder="(555) 123-4567"
                  className={errors.phone ? 'border-error' : ''}
                />
                {errors.phone && (
                  <p className="mt-1 text-sm text-error">{errors.phone}</p>
                )}
              </div>

              <div>
                <label className="block text-sm font-medium text-foreground mb-2">
                  Preferred Contact Method
                </label>
                <select
                  value={formData.preferredContactMethod}
                  onChange={(e) => handleInputChange('preferredContactMethod', e.target.value)}
                  className="w-full border border-border rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                >
                  {contactMethodOptions.map((option) => (
                    <option key={option.value} value={option.value}>
                      {option.label}
                    </option>
                  ))}
                </select>
              </div>

              <div>
                <DynamicDropdown
                  label="Primary Service"
                  value={formData.primaryService}
                  onChange={(value) => handleInputChange('primaryService', value)}
                  options={primaryServices}
                  onAddOption={handleAddPrimaryService}
                  placeholder="Select or add primary service..."
                  addNewText="Add new service"
                  disabled={loadingOptions}
                />
              </div>

              <div>
                <DynamicDropdown
                  label="Lead Source"
                  value={formData.leadSource}
                  onChange={(value) => handleInputChange('leadSource', value)}
                  options={leadSources}
                  onAddOption={handleAddLeadSource}
                  placeholder="Select or add lead source..."
                  addNewText="Add new source"
                  disabled={loadingOptions}
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-foreground mb-2">
                  Estimated Value
                </label>
                <Input
                  type="number"
                  step="0.01"
                  min="0"
                  value={formData.estimatedValue}
                  onChange={(e) => handleInputChange('estimatedValue', e.target.value)}
                  placeholder="0.00"
                />
                <p className="mt-1 text-xs text-text-secondary">
                  Optional: Estimated project value for pipeline tracking
                </p>
              </div>
            </div>
          </div>

          {/* Organization Information - only for organizations */}
          {formData.customerType === 'organization' && (
            <div>
              <h3 className="text-lg font-medium text-foreground mb-4">Organization Details</h3>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-foreground mb-2">
                    Website URL
                  </label>
                  <Input
                    type="url"
                    value={formData.website}
                    onChange={(e) => handleInputChange('website', e.target.value)}
                    placeholder="https://www.example.com"
                    className={errors.website ? 'border-error' : ''}
                  />
                  {errors.website && (
                    <p className="mt-1 text-sm text-error">{errors.website}</p>
                  )}
                </div>

                <div>
                  <label className="block text-sm font-medium text-foreground mb-2">
                    Industry
                  </label>
                  <select
                    value={formData.industry}
                    onChange={(e) => handleInputChange('industry', e.target.value)}
                    className="w-full border border-border rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                  >
                    {industryOptions.map((option) => (
                      <option key={option.value} value={option.value}>
                        {option.label}
                      </option>
                    ))}
                  </select>
                </div>

                <div>
                  <label className="block text-sm font-medium text-foreground mb-2">
                    Organization Size
                  </label>
                  <select
                    value={formData.organizationSize}
                    onChange={(e) => handleInputChange('organizationSize', e.target.value)}
                    className="w-full border border-border rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                  >
                    {organizationSizeOptions.map((option) => (
                      <option key={option.value} value={option.value}>
                        {option.label}
                      </option>
                    ))}
                  </select>
                </div>
              </div>
            </div>
          )}

          {/* Address Information */}
          <div>
            <h3 className="text-lg font-medium text-foreground mb-4">Service Address</h3>
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-foreground mb-2">
                  Street Address
                </label>
                <Input
                  type="text"
                  value={formData.address}
                  onChange={(e) => handleInputChange('address', e.target.value)}
                  placeholder="123 Main Street"
                  className={errors.address ? 'border-error' : ''}
                />
                {errors.address && (
                  <p className="mt-1 text-sm text-error">{errors.address}</p>
                )}
              </div>

              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <div>
                  <label className="block text-sm font-medium text-foreground mb-2">
                    City
                  </label>
                  <Input
                    type="text"
                    value={formData.city}
                    onChange={(e) => handleInputChange('city', e.target.value)}
                    placeholder="City"
                    className={errors.city ? 'border-error' : ''}
                  />
                  {errors.city && (
                    <p className="mt-1 text-sm text-error">{errors.city}</p>
                  )}
                </div>

                <div>
                  <label className="block text-sm font-medium text-foreground mb-2">
                    State
                  </label>
                  <Input
                    type="text"
                    value={formData.state}
                    onChange={(e) => handleInputChange('state', e.target.value)}
                    placeholder="State"
                    className={errors.state ? 'border-error' : ''}
                  />
                  {errors.state && (
                    <p className="mt-1 text-sm text-error">{errors.state}</p>
                  )}
                </div>

                <div>
                  <label className="block text-sm font-medium text-foreground mb-2">
                    ZIP Code
                  </label>
                  <Input
                    type="text"
                    value={formData.zipCode}
                    onChange={(e) => handleInputChange('zipCode', e.target.value)}
                    placeholder="12345"
                    className={errors.zipCode ? 'border-error' : ''}
                  />
                  {errors.zipCode && (
                    <p className="mt-1 text-sm text-error">{errors.zipCode}</p>
                  )}
                </div>
              </div>
            </div>
          </div>

          {/* Additional Notes */}
          <div>
            <label className="block text-sm font-medium text-foreground mb-2">
              Additional Notes
            </label>
            <textarea
              value={formData.notes}
              onChange={(e) => handleInputChange('notes', e.target.value)}
              placeholder="Any additional information about the customer..."
              rows={3}
              className="w-full border border-border rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent resize-none"
            />
          </div>

          {/* Form Actions */}
          <div className="flex items-center justify-end space-x-3 pt-6 border-t border-border">
            <Button variant="outline" onClick={onClose} disabled={isSubmitting}>
              Cancel
            </Button>
            <Button 
              variant="primary" 
              type="submit" 
              loading={isSubmitting}
              disabled={isSubmitting}
            >
              {isSubmitting ? 'Updating Customer...' : 'Update Customer'}
            </Button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default EditCustomerModal; 